package com.hyperether.goodjob.scenes.workspace

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.models.Plan
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.models.Workspace
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import org.lighthousegames.logging.logging

class WorkspaceViewModel(
    val repository: Repository
) : ViewModel() {

    val log = logging("WorkspaceViewModel")
    val workspaceList = mutableStateListOf<Workspace>()

    var workspaceId: MutableState<String> = mutableStateOf("")

    private val _currentPlan = mutableStateOf<Plan?>(null)
    val currentPlan: State<Plan?> = _currentPlan

    var roleMap: Map<String, String> = mutableMapOf()

    val showPopUpError: MutableState<Boolean> = mutableStateOf(false)
    val showLoader: MutableState<Boolean> = mutableStateOf(false)
    val errorText: MutableState<String> = mutableStateOf("")


    init {
        if (getPlatformChecker().isMobile()) {
            log.d("WorkspaceViewModel") { "init isMobile: ${getPlatformChecker().isMobile()}" }
            init()
        }
    }

    fun init() {
        viewModelScope.launch {
            repository.getWorkspacesFlow().collect {
                workspaceList.clear()
                workspaceList.addAll(it)
            }
        }
    }

    fun loadRemoteData() {
        viewModelScope.launch {
            repository.getToken().takeIf { it.isNotEmpty() }?.let {
                if (isSuperAdmin()) {
                    repository.getAllWorkspaces()
                } else {
                    repository.getUserWorkspaces()
                }
            }
        }
    }

    fun getWidQuery(): String {
        return repository.getWidQuery()
    }

    fun clearWedQuery() {
        repository.setWidQuery("")
    }

    fun addNewWorkspace(
        workspace: Workspace,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        viewModelScope.launch {
            val result = workspace.name?.let { name ->
                workspace.workspaceType?.let { type ->
                    repository.addWorkspace(name, type)
                }
            }
            if (result?.data != null) {
                if (!getPlatformChecker().isMobile()) {
                    init()
                }
                onSuccess.invoke()
            } else {
                onError(result?.text ?: stringResourcePlain(Res.string.unknown_error))
            }
        }
    }

    fun getAllJobs() {
        viewModelScope.launch {
            if (isEmployee()) {
                repository.getAllJobsForUser()
            } else {
                when (val result = repository.getAllJobsForWorkspace()) {
                    is Resource.Success -> {
                        println(result)
                    }

                    is Resource.Error -> {
                        println(result)
                    }
                }
            }
        }
    }

    fun getUser(): User? {
        return repository.getUser()
    }

    fun fetchCurrentPlan() {
        val user = getUser()
        if (user == null) {
            _currentPlan.value = null
            return
        }

        viewModelScope.launch {
            // todo fix null
            user.planId?.let {
                val plan = repository.getPlan(it)
                _currentPlan.value = plan
            } ?: run {
                repository.getWorkspacesFlow().collect { list ->
                    if (list.isNotEmpty()) {
                        val workspace = list[0]
                        workspace.planId?.let {
                            val plan = repository.getPlan(it)
                            _currentPlan.value = plan
                        }
                    }
                }
            }
        }
    }

    fun loadWorkspace(continueToDashboard: (Boolean) -> Unit) {
        saveWorkspaceId(workspaceId.value)
        if (isSuperAdmin()) {
            setWorkspace(
                workspaceId.value,
                onWorkspaceSet = {
                    if (it) {
                        getAllJobs()
                        updateLastAccessed()
                        continueToDashboard(true)
                    } else {
                        log.d("WorkspaceViewModel") { "setWorkspace failed" }
                        continueToDashboard(false)
                    }
                })
        } else {
            getAllJobs()
            updateLastAccessed()
            continueToDashboard(true)
        }
    }

    fun saveWorkspaceId(workspaceId: String) {
        return repository.saveWorkspaceId(workspaceId)
    }

    fun setWorkspace(
        workspaceId: String,
        onWorkspaceSet: (Boolean) -> Unit
    ) {
        viewModelScope.launch {
            when (repository.setWorkspace(workspaceId)) {
                is Resource.Error -> onWorkspaceSet(false)
                is Resource.Success -> onWorkspaceSet(true)
            }
        }
    }

    fun updateLastAccessed() {
        viewModelScope.launch {
            val lastAccessed =
                Clock.System.now().toEpochMilliseconds().toString()
            repository.updateWorkspaceLastAccessedById(lastAccessed, workspaceId.value)
        }
    }

    fun isEmployee(): Boolean {
        return repository.getUser()?.role.equals(Role.employee.name, ignoreCase = true)
    }

    fun isSuperAdmin(): Boolean {
        return repository.getUser()?.role.equals(Role.superAdmin.name, ignoreCase = true)
    }
}